import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  CircularProgress,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ResponsiveTable,
  ResponsiveModal,
  ConfirmDialog,
  CopyableText,
} from '../components/common';
import type { Column } from '../components/common';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import CategoryIcon from '@mui/icons-material/Category';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import { toast } from 'react-toastify';
import moment from 'moment-jalaali';
import workAssignmentService, {
  AdminWorkProfile,
  WorkCategory,
  ProfileCategory,
  ShiftDay,
  ShiftBreak,
  ShiftException,
  WorkCategoryCode,
  CATEGORY_LABELS,
  STATUS_LABELS,
  DAYS_OF_WEEK_FA,
  summarizeWeekly,
} from '../services/workAssignment.service';
import RefreshIcon from '@mui/icons-material/Refresh';
import GroupIcon from '@mui/icons-material/Group';
import WorkIcon from '@mui/icons-material/Work';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import './EntityManagement.css';

const PERSIAN_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];

/** Convert Latin/ASCII digits to Persian digits. */
const toPersianDigits = (s: string): string =>
  s.replace(/\d/g, (d) => PERSIAN_DIGITS[Number(d)]);

/** Convert Persian/Arabic digits to Latin (ASCII) digits. */
const toLatinDigits = (s: string): string =>
  s
    .replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
    .replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)));

const formatMinute = (m: number) => {
  const clamped = m % 1440;
  const h = Math.floor(clamped / 60);
  const mm = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};

const formatMinuteFa = (m: number) => toPersianDigits(formatMinute(m));

/** Parse "HH:MM" → minutes (0..1440). Returns null on invalid input. */
const parseTime = (s: string): number | null => {
  const latin = toLatinDigits(s.trim());
  const m = /^(\d{1,2}):(\d{2})$/.exec(latin);
  if (!m) return null;
  const h = Number(m[1]);
  const mm = Number(m[2]);
  if (h < 0 || h > 24 || mm < 0 || mm > 59) return null;
  return h * 60 + mm;
};

/**
 * Ensure a weekly schedule has all 7 days present (in order).
 * Also normalize wrap periods (endMinute > 1440) back to unwrapped form (endMinute < startMinute)
 * so the time picker displays the actual end time ("02:00" instead of "26:00"). The save handler
 * reconstructs the wrap when persisting.
 */
const normalizeSchedule = (weekly: ShiftDay[] | undefined): ShiftDay[] => {
  const byDay = new Map<number, ShiftDay>();
  (weekly || []).forEach((d) =>
    byDay.set(d.dayOfWeek, {
      ...d,
      periods: (d.periods || []).map((p) => ({
        startMinute: p.startMinute,
        endMinute: p.endMinute > 1440 ? p.endMinute - 1440 : p.endMinute,
      })),
    })
  );
  return Array.from({ length: 7 }, (_, d) => byDay.get(d) ?? { dayOfWeek: d, periods: [] });
};

/**
 * Normalize exceptions for editing: unwrap wrap periods (endMinute > 1440) back to the actual
 * end time so the time pickers show "02:00" instead of "26:00". The save handler reconstructs
 * the wrap (end <= start → +1440) when persisting — same convention as the weekly editor.
 */
const normalizeExceptions = (list: ShiftException[] | undefined): ShiftException[] =>
  (list || []).map((ex) => ({
    date: ex.date,
    type: ex.type,
    reason: ex.reason,
    periods: ex.periods
      ? ex.periods.map((p) => ({
          startMinute: p.startMinute,
          endMinute: p.endMinute > 1440 ? p.endMinute - 1440 : p.endMinute,
        }))
      : undefined,
  }));

/**
 * Predefined shift templates.
 * For shifts wrapping past midnight, `end` > 1440.
 */
type ShiftTemplate = {
  key: string;
  labelFa: string;
  start: number;
  end: number;
};

const SHIFT_TEMPLATES: ShiftTemplate[] = [
  { key: 'morning', labelFa: 'صبح (۶–۱۴)', start: 360, end: 840 },
  { key: 'day', labelFa: 'روز (۸–۱۶)', start: 480, end: 960 },
  { key: 'evening', labelFa: 'عصر (۱۶–۲۴)', start: 960, end: 1440 },
  { key: 'night', labelFa: 'شب (۲۲–۶ فردا)', start: 1320, end: 1800 },
];

const DAY_SET_WORKWEEK = [0, 1, 2, 3, 4]; // Sat..Wed

/** Sort periods by startMinute and return a new array. */
const sortPeriods = (periods: { startMinute: number; endMinute: number }[]) =>
  periods.slice().sort((a, b) => a.startMinute - b.startMinute);

/** Detect overlap between periods (assumes sorted ascending by startMinute). */
const hasOverlap = (periods: { startMinute: number; endMinute: number }[]): boolean => {
  const sorted = sortPeriods(periods);
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].startMinute < sorted[i - 1].endMinute) return true;
  }
  return false;
};

/**
 * Per-day shift editor: a small card with a list of time ranges the admin works.
 * Multiple shifts per day supported (e.g. 08:00-12:00 + 13:00-17:00 + 19:00-23:00).
 * Wrap-midnight: if `end < start`, the UI shows "تا فردا" and stores `endMinute = endInputMinute + 1440`.
 */
/** Total minutes of on-shift time for a day (handles wrap by adding 1440 to end if end <= start). */
const dayTotalMinutes = (periods: { startMinute: number; endMinute: number }[]) =>
  periods.reduce((sum, p) => {
    const end = p.endMinute <= p.startMinute ? p.endMinute + 1440 : p.endMinute;
    return sum + (end - p.startMinute);
  }, 0);

/** Format total minutes as "H:MM ساعت" or "M دقیقه". */
const formatDuration = (mins: number) => {
  if (mins <= 0) return '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m} دقیقه`;
  if (m === 0) return `${h} ساعت`;
  return `${h}:${String(m).padStart(2, '0')} ساعت`;
};

/** Time input that displays Persian digits and accepts HH:MM. */
const PersianTimeInput: React.FC<{
  value: number;
  onChange: (minutes: number) => void;
}> = ({ value, onChange }) => {
  const display = formatMinuteFa(value);
  const [local, setLocal] = useState(display);
  useEffect(() => {
    setLocal(formatMinuteFa(value));
  }, [value]);

  const commit = (raw: string) => {
    const latin = toLatinDigits(raw);
    const m = /^(\d{1,2}):?(\d{0,2})$/.exec(latin);
    if (!m) { setLocal(formatMinuteFa(value)); return; }
    let h = Number(m[1]);
    let mm = Number(m[2] || '0');
    if (isNaN(h)) h = 0;
    if (isNaN(mm)) mm = 0;
    if (h > 23) h = 23;
    if (mm > 59) mm = 59;
    onChange(h * 60 + mm);
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      value={local}
      onChange={(e) => {
        // Allow user typing Persian or Latin digits + one colon
        const raw = e.target.value;
        const cleaned = raw.replace(/[^۰-۹0-9:]/g, '').slice(0, 5);
        setLocal(toPersianDigits(cleaned));
      }}
      onBlur={() => commit(local)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          commit(local);
          (e.target as HTMLInputElement).blur();
        }
      }}
      placeholder="۰۰:۰۰"
      style={{
        flex: '1 1 0', minWidth: 0, width: '100%',
        height: 34, padding: '0 6px',
        border: '1px solid var(--color-border)', borderRadius: 6,
        fontSize: 13, fontWeight: 600, textAlign: 'center',
        background: 'var(--color-bg-paper)', color: 'var(--color-text-primary)',
        boxSizing: 'border-box', fontFamily: 'Vazirmatn, sans-serif',
        letterSpacing: 1,
      }}
    />
  );
};

/** Jalali date picker — input with Persian digits + dropdown calendar. */
const JalaliDatePicker: React.FC<{
  value: string; // ISO date string
  onChange: (iso: string) => void;
}> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const jm = value ? moment(value) : moment();
  const jy = jm.jYear();
  const jmon = jm.jMonth();
  const jd = jm.jDate();
  const displayStr = value ? toPersianDigits(jm.format('jYYYY/jMM/jDD')) : 'انتخاب تاریخ';

  const [viewYear, setViewYear] = useState(jy);
  const [viewMonth, setViewMonth] = useState(jmon);

  useEffect(() => {
    if (open) {
      setViewYear(jy);
      setViewMonth(jmon);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const monthNames = ['فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور', 'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'];
  const weekdays = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];

  // Compute days in month
  const daysInMonth = viewMonth < 6 ? 31 : viewMonth < 11 ? 30 : moment.jIsLeapYear(viewYear) ? 30 : 29;
  // Get first day of month weekday (jalali: saturday = 0)
  const firstDay = moment(`${viewYear}/${viewMonth + 1}/1`, 'jYYYY/jM/jD');
  const firstWeekday = (firstDay.day() + 1) % 7; // convert Sun=0 to Sat=0

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const selectDay = (d: number) => {
    const iso = moment(`${viewYear}/${viewMonth + 1}/${d}`, 'jYYYY/jM/jD').toISOString();
    onChange(iso);
    setOpen(false);
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          width: '100%', minWidth: 160,
          padding: '0 12px', height: 38,
          background: 'var(--color-bg-paper)',
          border: '1px solid var(--color-border)',
          borderRadius: 6,
          cursor: 'pointer',
          fontSize: 13, fontFamily: 'inherit',
          color: value ? 'var(--color-text-primary)' : 'var(--color-text-disabled)',
          justifyContent: 'space-between',
        }}
      >
        <span>{displayStr}</span>
        <EventBusyIcon sx={{ fontSize: 16, color: 'var(--color-text-secondary)' }} />
      </button>
      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          right: 0,
          zIndex: 10,
          background: 'var(--color-bg-paper)',
          border: '1px solid var(--color-border)',
          borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          padding: 10,
          width: 260,
        }}>
          {/* Month navigation */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 8,
          }}>
            <button onClick={prevMonth} style={{
              width: 28, height: 28, border: 'none', background: 'transparent',
              cursor: 'pointer', borderRadius: 6, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              color: 'var(--color-text-secondary)',
            }}>‹</button>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>
              {monthNames[viewMonth]} {toPersianDigits(String(viewYear))}
            </div>
            <button onClick={nextMonth} style={{
              width: 28, height: 28, border: 'none', background: 'transparent',
              cursor: 'pointer', borderRadius: 6, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              color: 'var(--color-text-secondary)',
            }}>›</button>
          </div>
          {/* Weekdays */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2,
            marginBottom: 4,
          }}>
            {weekdays.map((w) => (
              <div key={w} style={{
                fontSize: 10, textAlign: 'center', padding: 4,
                color: 'var(--color-text-secondary)', fontWeight: 600,
              }}>{w}</div>
            ))}
          </div>
          {/* Days */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
            {cells.map((d, i) => {
              if (d === null) return <div key={i} />;
              const isSelected = value && viewYear === jy && viewMonth === jmon && d === jd;
              const isToday = viewYear === moment().jYear() && viewMonth === moment().jMonth() && d === moment().jDate();
              return (
                <button
                  key={i}
                  onClick={() => selectDay(d)}
                  style={{
                    height: 30, border: 'none', borderRadius: 6,
                    cursor: 'pointer', fontSize: 12,
                    background: isSelected ? 'var(--color-primary)' : isToday ? 'var(--color-primary-bg)' : 'transparent',
                    color: isSelected ? '#fff' : isToday ? 'var(--color-primary)' : 'var(--color-text-primary)',
                    fontWeight: isSelected || isToday ? 700 : 400,
                    fontFamily: 'inherit',
                  }}
                  onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = isToday ? 'var(--color-primary-bg)' : 'var(--color-bg-default)'; }}
                  onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = isToday ? 'var(--color-primary-bg)' : 'transparent'; }}
                >
                  {toPersianDigits(String(d))}
                </button>
              );
            })}
          </div>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--color-divider)',
          }}>
            <button
              onClick={() => selectDay(moment().jDate())}
              style={{
                padding: '4px 10px', border: 'none', borderRadius: 6,
                cursor: 'pointer', fontSize: 11, fontWeight: 500,
                background: 'var(--color-bg-default)',
                color: 'var(--color-text-primary)', fontFamily: 'inherit',
              }}
            >
              امروز
            </button>
            <button
              onClick={() => setOpen(false)}
              style={{
                padding: '4px 10px', border: 'none', borderRadius: 6,
                cursor: 'pointer', fontSize: 11, fontWeight: 500,
                background: 'transparent',
                color: 'var(--color-text-secondary)', fontFamily: 'inherit',
              }}
            >
              بستن
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const EXCEPTION_TYPE_META: Record<string, { label: string; color: string; bg: string; icon: string }> = {
  leave: { label: 'مرخصی', color: 'var(--color-error-dark)', bg: 'var(--color-error-bg)', icon: '🌴' },
  extra: { label: 'اضافه‌کاری', color: 'var(--color-primary)', bg: 'var(--color-primary-bg)', icon: '⚡' },
  holiday: { label: 'تعطیلی', color: 'var(--color-primary)', bg: 'var(--color-primary-bg)', icon: '🎉' },
};

const DayShiftCard: React.FC<{
  dayIndex: number;
  periods: { startMinute: number; endMinute: number }[];
  onChange: (p: { startMinute: number; endMinute: number }[]) => void;
  onCopyToAll: () => void;
  onApplyTemplate: (t: ShiftTemplate) => void;
  onClear: () => void;
}> = ({ dayIndex, periods, onChange, onCopyToAll, onApplyTemplate, onClear }) => {
  const updatePeriod = (idx: number, field: 'startMinute' | 'endMinute', value: string) => {
    const m = parseTime(value);
    if (m === null) return;
    const next = periods.map((p, i) => (i === idx ? { ...p, [field]: m } : p));
    onChange(next);
  };

  const deletePeriod = (idx: number) => {
    onChange(periods.filter((_, i) => i !== idx));
  };

  const addPeriod = () => {
    const last = sortPeriods(periods).slice(-1)[0];
    const defaultStart = last ? Math.min(last.endMinute + 30, 1320) : 540;
    const defaultEnd = Math.min(defaultStart + 480, 1440);
    onChange([...periods, { startMinute: defaultStart, endMinute: defaultEnd }]);
  };

  const overlap = hasOverlap(periods);
  const total = dayTotalMinutes(periods);
  const isWeekend = dayIndex === 5 || dayIndex === 6;
  const hasShifts = periods.length > 0;

  return (
    <div style={{
      background: 'var(--color-bg-paper)',
      border: `2px solid ${overlap ? 'var(--color-error-dark)' : hasShifts ? 'var(--color-primary)' : 'var(--color-divider)'}`,
      borderRadius: 10,
      padding: 12,
      position: 'relative',
      transition: 'all 0.2s',
      boxSizing: 'border-box',
      minWidth: 0,
      overflow: 'hidden',
      ...(isWeekend && !hasShifts && { background: 'var(--color-bg-default)' }),
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        paddingBottom: 10, marginBottom: 10,
        borderBottom: '1px solid var(--color-divider)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: hasShifts ? 'var(--color-primary)' : 'var(--color-bg-default)',
            color: hasShifts ? '#fff' : 'var(--color-text-secondary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 700,
          }}>
            {DAYS_OF_WEEK_FA[dayIndex].charAt(0)}
          </div>
          <div>
            <div style={{
              fontSize: 14, fontWeight: 700,
              color: hasShifts ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
            }}>
              {DAYS_OF_WEEK_FA[dayIndex]}
            </div>
            <div style={{
              fontSize: 11, lineHeight: 1.2,
              color: hasShifts ? 'var(--color-primary)' : 'var(--color-text-secondary)',
              fontWeight: hasShifts ? 600 : 400,
            }}>
              {hasShifts ? `${periods.length} شیفت · ${formatDuration(total)}` : 'بدون شیفت'}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 2 }}>
          <Tooltip title="کپی به بقیه روزها">
            <span>
              <button
                onClick={onCopyToAll}
                disabled={!hasShifts}
                style={{
                  width: 26, height: 26, borderRadius: 6, border: 'none',
                  background: 'transparent', cursor: hasShifts ? 'pointer' : 'not-allowed',
                  color: hasShifts ? 'var(--color-text-secondary)' : 'var(--color-text-disabled)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: hasShifts ? 1 : 0.4,
                }}
                onMouseEnter={(e) => { if (hasShifts) e.currentTarget.style.background = 'var(--color-bg-default)'; }}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <ContentCopyIcon fontSize="small" />
              </button>
            </span>
          </Tooltip>
          <Tooltip title="پاک‌کردن این روز">
            <span>
              <button
                onClick={onClear}
                disabled={!hasShifts}
                style={{
                  width: 26, height: 26, borderRadius: 6, border: 'none',
                  background: 'transparent', cursor: hasShifts ? 'pointer' : 'not-allowed',
                  color: hasShifts ? 'var(--color-error-dark)' : 'var(--color-text-disabled)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: hasShifts ? 1 : 0.4,
                }}
                onMouseEnter={(e) => { if (hasShifts) e.currentTarget.style.background = 'var(--color-error-bg)'; }}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <CloseIcon fontSize="small" />
              </button>
            </span>
          </Tooltip>
        </div>
      </div>

      {/* Template chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
        {SHIFT_TEMPLATES.map((t) => {
          const isSelected = periods.some(
            (p) => p.startMinute === t.start && (p.endMinute === t.end || p.endMinute === t.end - 1440)
          );
          return (
            <button
              key={t.key}
              onClick={() => onApplyTemplate(t)}
              style={{
                padding: '3px 8px',
                background: isSelected ? 'var(--color-primary)' : 'transparent',
                border: `1px solid ${isSelected ? 'var(--color-primary)' : 'var(--color-border)'}`,
                borderRadius: 10,
                fontSize: 10, fontWeight: isSelected ? 600 : 500,
                cursor: 'pointer',
                color: isSelected ? '#fff' : 'var(--color-text-secondary)',
                fontFamily: 'inherit',
                transition: 'all 0.15s',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = 'var(--color-primary)';
                  e.currentTarget.style.color = 'var(--color-primary)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.borderColor = 'var(--color-border)';
                  e.currentTarget.style.color = 'var(--color-text-secondary)';
                }
              }}
            >
              {isSelected && <CheckCircleIcon sx={{ fontSize: 11 }} />}
              {t.labelFa}
            </button>
          );
        })}
      </div>

      {/* Periods */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {periods.length === 0 && (
          <div style={{
            padding: '20px 12px', textAlign: 'center',
            color: 'var(--color-text-disabled)', fontSize: 12,
            border: '1px dashed var(--color-divider)', borderRadius: 8,
          }}>
            شیفتی تعریف نشده
          </div>
        )}
        {periods.map((p, idx) => {
          const wraps = p.endMinute <= p.startMinute;
          const duration = (wraps ? p.endMinute + 1440 : p.endMinute) - p.startMinute;
          const startStr = formatMinute(p.startMinute);
          const endStr = formatMinute(p.endMinute);
          return (
            <div key={idx} style={{
              background: 'var(--color-bg-default)',
              border: `1px solid ${wraps ? 'var(--color-primary-500)' : 'var(--color-divider)'}`,
              borderRadius: 8,
              padding: 10,
              boxSizing: 'border-box',
              overflow: 'hidden',
            }}>
              {/* Row 1: Time inputs (Persian digits) */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                minWidth: 0,
              }}>
                <PersianTimeInput
                  value={p.startMinute}
                  onChange={(v) => onChange(periods.map((pp, i) => (i === idx ? { ...pp, startMinute: v } : pp)))}
                />
                <span style={{
                  fontSize: 13, color: 'var(--color-text-secondary)', fontWeight: 600,
                  flexShrink: 0,
                }}>←</span>
                <PersianTimeInput
                  value={p.endMinute}
                  onChange={(v) => onChange(periods.map((pp, i) => (i === idx ? { ...pp, endMinute: v } : pp)))}
                />
              </div>

              {/* Row 2: Duration + wrap + delete */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6, marginTop: 8,
                flexWrap: 'wrap', direction: 'rtl',
              }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '2px 8px',
                  background: 'var(--color-bg-paper)',
                  border: '1px solid var(--color-divider)',
                  borderRadius: 10,
                  fontSize: 10, fontWeight: 500,
                  color: 'var(--color-text-secondary)',
                }}>
                  <AccessTimeIcon sx={{ fontSize: 12 }} />
                  {formatDuration(duration)}
                </span>
                {wraps && (
                  <span style={{
                    padding: '2px 8px',
                    background: 'var(--color-primary-bg)',
                    color: 'var(--color-primary)',
                    borderRadius: 10,
                    fontSize: 10, fontWeight: 600,
                  }}>
                    تا فردا
                  </span>
                )}
                <div style={{ flex: 1 }} />
                <button
                  onClick={() => deletePeriod(idx)}
                  style={{
                    width: 26, height: 26, borderRadius: 6, border: 'none',
                    background: 'transparent', cursor: 'pointer',
                    color: 'var(--color-error-dark)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-error-bg)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  title="حذف شیفت"
                >
                  <DeleteIcon fontSize="small" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add button */}
      <button
        onClick={addPeriod}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          width: '100%', marginTop: 10,
          padding: '8px 12px',
          background: 'transparent',
          border: '1px dashed var(--color-border)',
          borderRadius: 8,
          fontSize: 12, fontWeight: 500,
          cursor: 'pointer',
          color: 'var(--color-text-secondary)',
          fontFamily: 'inherit',
          transition: 'all 0.15s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'var(--color-primary)';
          e.currentTarget.style.color = 'var(--color-primary)';
          e.currentTarget.style.background = 'var(--color-primary-bg)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'var(--color-border)';
          e.currentTarget.style.color = 'var(--color-text-secondary)';
          e.currentTarget.style.background = 'transparent';
        }}
      >
        <AddIcon fontSize="small" />
        افزودن شیفت
      </button>

      {overlap && (
        <div style={{
          marginTop: 8, padding: '6px 10px',
          background: 'var(--color-error-bg)', color: 'var(--color-error-dark)',
          borderRadius: 6, fontSize: 11,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          ⚠ بازه‌های شیفت با هم تداخل دارند
        </div>
      )}
    </div>
  );
};

/**
 * Weekly shift editor — 7 day cards.
 * State is a normalized `ShiftDay[]` of length 7 (one per day, periods may be empty).
 */
const WeeklyShiftEditor: React.FC<{
  schedule: ShiftDay[];
  onChange: (s: ShiftDay[]) => void;
}> = ({ schedule, onChange }) => {
  const updateDay = (dayIndex: number, periods: { startMinute: number; endMinute: number }[]) => {
    const next = schedule.map((d) =>
      d.dayOfWeek === dayIndex ? { ...d, periods } : d
    );
    onChange(next);
  };

  const copyDayTo = (sourceDay: number, targetDays: number[]) => {
    const source = schedule.find((d) => d.dayOfWeek === sourceDay);
    const next = schedule.map((d) =>
      targetDays.includes(d.dayOfWeek)
        ? { ...d, periods: (source?.periods || []).map((p) => ({ ...p })) }
        : d
    );
    onChange(next);
  };

  const applyTemplateToDays = (t: ShiftTemplate, targetDays: number[]) => {
    const next = schedule.map((d) => {
      if (!targetDays.includes(d.dayOfWeek)) return d;
      const newPeriod = { startMinute: t.start, endMinute: t.end };
      // Merge into existing periods, avoiding duplicates.
      const existing = (d.periods || []).filter(
        (p) => !(p.startMinute === t.start && p.endMinute === t.end)
      );
      return { ...d, periods: sortPeriods([...existing, newPeriod]) };
    });
    onChange(next);
  };

  const clearAll = () => {
    onChange(schedule.map((d) => ({ ...d, periods: [] })));
  };

  const allDays = [0, 1, 2, 3, 4, 5, 6];

  // Compute weekly stats.
  const totalWeekMinutes = schedule.reduce((sum, d) => sum + dayTotalMinutes(d.periods), 0);
  const activeDays = schedule.filter((d) => d.periods.length > 0);
  const activeDaysCount = activeDays.length;
  const inactiveDays = schedule.filter((d) => d.periods.length === 0);
  const totalShiftsCount = schedule.reduce((sum, d) => sum + d.periods.length, 0);

  return (
    <div>
      {/* Top summary — 3 big stat boxes */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 12,
        marginBottom: 16,
      }}>
        <div style={{
          background: 'var(--color-bg-paper)',
          border: '1px solid var(--color-divider)',
          borderRadius: 10,
          padding: 14,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: 'var(--color-primary-bg)', color: 'var(--color-primary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <ScheduleIcon />
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>ساعت کار هفتگی</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-text-primary)', lineHeight: 1.2 }}>
              {formatDuration(totalWeekMinutes) || '۰ ساعت'}
            </div>
          </div>
        </div>

        <div style={{
          background: 'var(--color-bg-paper)',
          border: '1px solid var(--color-divider)',
          borderRadius: 10,
          padding: 14,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: activeDaysCount > 0 ? 'var(--color-success-bg)' : 'var(--color-error-bg)',
            color: activeDaysCount > 0 ? 'var(--color-success-dark)' : 'var(--color-error-dark)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <CheckCircleIcon />
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>روزهای فعال</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-text-primary)', lineHeight: 1.2 }}>
              {activeDaysCount} <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--color-text-secondary)' }}>از ۷</span>
            </div>
          </div>
        </div>

        <div style={{
          background: 'var(--color-bg-paper)',
          border: '1px solid var(--color-divider)',
          borderRadius: 10,
          padding: 14,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: 'var(--color-primary-bg)', color: 'var(--color-primary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <AccessTimeIcon />
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>کل شیفت‌ها</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-text-primary)', lineHeight: 1.2 }}>
              {totalShiftsCount}
            </div>
          </div>
        </div>
      </div>

      {/* Info banner if no shifts */}
      {activeDaysCount === 0 && (
        <div style={{
          display: 'flex', gap: 10, alignItems: 'flex-start',
          padding: '10px 14px',
          background: 'rgba(245, 158, 11, 0.1)',
          color: 'var(--color-warning-dark)',
          borderRadius: 8,
          marginBottom: 16,
          fontSize: 12,
        }}>
          <ScheduleIcon fontSize="small" style={{ marginTop: 2 }} />
          <div>
            <div style={{ fontWeight: 600, marginBottom: 2 }}>هنوز هیچ شیفتی تعریف نشده</div>
            <div>برای اینکه ادمین در صف‌ها دیده شود، حداقل یک بازه شیفت تعیین کنید.</div>
          </div>
        </div>
      )}

      {/* Quick actions bar */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center',
        padding: 12,
        background: 'var(--color-bg-default)',
        border: '1px solid var(--color-divider)',
        borderRadius: 10,
        marginBottom: 16,
      }}>
        <span style={{
          fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)',
          marginInlineEnd: 4,
        }}>
          اعمال سریع شنبه–چهارشنبه:
        </span>
        {SHIFT_TEMPLATES.map((t) => (
          <button
            key={t.key}
            onClick={() => applyTemplateToDays(t, DAY_SET_WORKWEEK)}
            style={{
              padding: '6px 14px',
              background: 'var(--color-bg-paper)',
              border: '1px solid var(--color-border)',
              borderRadius: 6,
              fontSize: 12, fontWeight: 500,
              cursor: 'pointer',
              color: 'var(--color-text-primary)',
              fontFamily: 'inherit',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--color-primary)';
              e.currentTarget.style.color = 'var(--color-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--color-border)';
              e.currentTarget.style.color = 'var(--color-text-primary)';
            }}
          >
            {t.labelFa}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        {totalShiftsCount > 0 && (
          <button
            onClick={clearAll}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '6px 12px',
              background: 'transparent',
              border: '1px solid transparent',
              borderRadius: 6,
              fontSize: 12, fontWeight: 500,
              cursor: 'pointer',
              color: 'var(--color-error-dark)',
              fontFamily: 'inherit',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-error-bg)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <CloseIcon fontSize="small" />
            پاک‌کردن همه
          </button>
        )}
      </div>

      {/* 7 day cards grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gap: 12,
      }}>
        {schedule.map((d) => (
          <DayShiftCard
            key={d.dayOfWeek}
            dayIndex={d.dayOfWeek}
            periods={d.periods}
            onChange={(p) => updateDay(d.dayOfWeek, p)}
            onCopyToAll={() =>
              copyDayTo(
                d.dayOfWeek,
                allDays.filter((x) => x !== d.dayOfWeek)
              )
            }
            onApplyTemplate={(t) => applyTemplateToDays(t, [d.dayOfWeek])}
            onClear={() => updateDay(d.dayOfWeek, [])}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Breaks editor — rest periods inside shifts (backend: shift.breaks).
 * Each break: optional dayOfWeek (undefined = every day), startMinute/endMinute within the
 * same day (0..1440 — backend schema does not allow breaks to wrap past midnight), optional label.
 */
const BreaksEditor: React.FC<{
  breaks: ShiftBreak[];
  onChange: (b: ShiftBreak[]) => void;
}> = ({ breaks, onChange }) => {
  const update = (idx: number, patch: Partial<ShiftBreak>) =>
    onChange(breaks.map((b, i) => (i === idx ? { ...b, ...patch } : b)));

  const remove = (idx: number) => onChange(breaks.filter((_, i) => i !== idx));

  const addBreak = () => onChange([...breaks, { startMinute: 720, endMinute: 780, label: '' }]);

  return (
    <div style={{ marginTop: 24 }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, padding: '14px 16px',
        background: 'var(--color-bg-default)',
        border: '1px solid var(--color-divider)',
        borderRadius: 10,
        marginBottom: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 8,
            background: 'var(--color-warning-bg)', color: 'var(--color-warning-dark)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <AccessTimeIcon fontSize="small" />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text-primary)' }}>
              زمان‌های استراحت ({toPersianDigits(String(breaks.length))})
            </div>
            <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
              بازه‌هایی در میانه شیفت که ادمین در دسترس نیست (مثل ناهار) — حداکثر تا پایان همان روز
            </div>
          </div>
        </div>
        <button
          onClick={addBreak}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 14px',
            background: 'var(--color-primary)', color: '#fff',
            border: 'none', borderRadius: 8,
            fontSize: 13, fontWeight: 500,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-primary-dark)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--color-primary)')}
        >
          <AddIcon fontSize="small" />
          افزودن استراحت
        </button>
      </div>

      {breaks.length === 0 && (
        <div style={{
          padding: '20px 12px', textAlign: 'center',
          color: 'var(--color-text-disabled)', fontSize: 12,
          border: '1px dashed var(--color-divider)', borderRadius: 8,
        }}>
          استراحتی تعریف نشده
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {breaks.map((b, idx) => {
          const invalid = b.endMinute <= b.startMinute;
          return (
            <div
              key={idx}
              style={{
                background: 'var(--color-bg-paper)',
                border: `1px solid ${invalid ? 'var(--color-error-dark)' : 'var(--color-divider)'}`,
                borderRadius: 10,
                padding: 12,
              }}
            >
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
                {/* Day of week (optional = every day) */}
                <div style={{ flex: '1 1 130px', minWidth: 0 }}>
                  <label style={{
                    display: 'block', fontSize: 11, fontWeight: 500,
                    color: 'var(--color-text-secondary)', marginBottom: 4,
                  }}>
                    روز هفته
                  </label>
                  <select
                    value={b.dayOfWeek === undefined || b.dayOfWeek === null ? '' : String(b.dayOfWeek)}
                    onChange={(e) =>
                      update(idx, { dayOfWeek: e.target.value === '' ? undefined : Number(e.target.value) })
                    }
                    style={{
                      width: '100%', height: 34, padding: '0 10px',
                      border: '1px solid var(--color-border)', borderRadius: 6,
                      fontSize: 13, fontFamily: 'inherit',
                      background: 'var(--color-bg-paper)',
                      color: 'var(--color-text-primary)',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="">همه روزها</option>
                    {DAYS_OF_WEEK_FA.map((name, d) => (
                      <option key={d} value={d}>{name}</option>
                    ))}
                  </select>
                </div>

                {/* Time range */}
                <div style={{ flex: '2 1 180px', minWidth: 0 }}>
                  <label style={{
                    display: 'block', fontSize: 11, fontWeight: 500,
                    color: 'var(--color-text-secondary)', marginBottom: 4,
                  }}>
                    بازه استراحت
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                    <PersianTimeInput
                      value={b.startMinute}
                      onChange={(v) => update(idx, { startMinute: v })}
                    />
                    <span style={{ fontSize: 13, color: 'var(--color-text-secondary)', fontWeight: 600, flexShrink: 0 }}>←</span>
                    <PersianTimeInput
                      value={b.endMinute}
                      onChange={(v) => update(idx, { endMinute: v })}
                    />
                  </div>
                </div>

                {/* Label */}
                <div style={{ flex: '2 1 140px', minWidth: 0 }}>
                  <label style={{
                    display: 'block', fontSize: 11, fontWeight: 500,
                    color: 'var(--color-text-secondary)', marginBottom: 4,
                  }}>
                    عنوان (اختیاری)
                  </label>
                  <input
                    type="text"
                    value={b.label || ''}
                    onChange={(e) => update(idx, { label: e.target.value })}
                    placeholder="مثلاً ناهار..."
                    style={{
                      width: '100%', height: 34, padding: '0 10px',
                      border: '1px solid var(--color-border)', borderRadius: 6,
                      fontSize: 13, fontFamily: 'inherit',
                      background: 'var(--color-bg-paper)',
                      color: 'var(--color-text-primary)',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                {/* Delete */}
                <button
                  onClick={() => remove(idx)}
                  style={{
                    width: 34, height: 34, borderRadius: 8, border: 'none',
                    background: 'transparent', cursor: 'pointer',
                    color: 'var(--color-error-dark)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-error-bg)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  title="حذف استراحت"
                >
                  <DeleteIcon fontSize="small" />
                </button>
              </div>

              {invalid && (
                <div style={{
                  marginTop: 8, padding: '6px 10px',
                  background: 'var(--color-error-bg)', color: 'var(--color-error-dark)',
                  borderRadius: 6, fontSize: 11,
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  ⚠ زمان پایان باید بعد از زمان شروع باشد (استراحت نمی‌تواند به روز بعد برسد)
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ProfileEditor: React.FC<{
  open: boolean;
  onClose: () => void;
  initial?: AdminWorkProfile;
  categories: WorkCategory[];
  onSaved: () => void;
}> = ({ open, onClose, initial, categories, onSaved }) => {
  const [adminUsers, setAdminUsers] = useState<Array<{ _id: string; name: string; email: string }>>(
    []
  );
  const [userId, setUserId] = useState<string>('');
  const [isActive, setIsActive] = useState(true);
  const [notes, setNotes] = useState('');
  const [catEntries, setCatEntries] = useState<ProfileCategory[]>([]);
  const [weeklySchedule, setWeeklySchedule] = useState<ShiftDay[]>(normalizeSchedule([]));
  const [breaks, setBreaks] = useState<ShiftBreak[]>([]);
  const [saving, setSaving] = useState(false);
  const [exceptions, setExceptions] = useState<ShiftException[]>([]);
  const [version, setVersion] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!open) return;
    workAssignmentService.listAdminUsers().then(setAdminUsers).catch(() => setAdminUsers([]));
  }, [open]);

  useEffect(() => {
    if (initial) {
      const uid = typeof initial.userId === 'string' ? initial.userId : initial.userId._id;
      setUserId(uid);
      setIsActive(initial.isActive);
      setNotes(initial.notes || '');
      setCatEntries(initial.categories || []);
      setWeeklySchedule(normalizeSchedule(initial.shift.weeklySchedule));
      setBreaks((initial.shift.breaks || []).map((b) => ({ ...b })));
      setExceptions(normalizeExceptions(initial.exceptions));
      setVersion(initial.version);
    } else {
      setUserId('');
      setIsActive(true);
      setNotes('');
      setCatEntries([]);
      setWeeklySchedule(normalizeSchedule([]));
      setBreaks([]);
      setExceptions([]);
      setVersion(undefined);
    }
  }, [initial, open]);

  const [tab, setTab] = useState(0);
  const [catSearch, setCatSearch] = useState('');

  const toggleCategory = (code: WorkCategoryCode) => {
    const has = catEntries.some((c) => c.code === code);
    if (has) {
      setCatEntries(catEntries.filter((c) => c.code !== code));
    } else {
      setCatEntries([...catEntries, { code, priority: 3, maxConcurrentLoad: 0 }]);
    }
  };

  const updateCategoryEntry = (code: WorkCategoryCode, patch: Partial<ProfileCategory>) => {
    setCatEntries(catEntries.map((c) => (c.code === code ? { ...c, ...patch } : c)));
  };

  /* --- Exception period helpers ('extra' exceptions carry time periods) --- */
  const updateExceptionPeriod = (
    exIdx: number,
    pIdx: number,
    patch: Partial<{ startMinute: number; endMinute: number }>
  ) => {
    setExceptions(
      exceptions.map((ex, i) =>
        i === exIdx
          ? { ...ex, periods: (ex.periods || []).map((p, j) => (j === pIdx ? { ...p, ...patch } : p)) }
          : ex
      )
    );
  };

  const addExceptionPeriod = (exIdx: number) => {
    setExceptions(
      exceptions.map((ex, i) => {
        if (i !== exIdx) return ex;
        const periods = ex.periods || [];
        const last = sortPeriods(periods).slice(-1)[0];
        const start = last ? Math.min(last.endMinute + 30, 1320) : 540;
        return { ...ex, periods: [...periods, { startMinute: start, endMinute: Math.min(start + 240, 1439) }] };
      })
    );
  };

  const removeExceptionPeriod = (exIdx: number, pIdx: number) => {
    setExceptions(
      exceptions.map((ex, i) =>
        i === exIdx ? { ...ex, periods: (ex.periods || []).filter((_, j) => j !== pIdx) } : ex
      )
    );
  };

  const handleSave = async () => {
    if (!userId) {
      toast.error('ادمین را انتخاب کنید');
      return;
    }
    if (catEntries.length === 0) {
      toast.error('حداقل یک حوزه کاری لازم است');
      return;
    }
    setSaving(true);
    try {
      // Normalize for save:
      // - Drop days with no periods.
      // - If end <= start (user entered a time that wraps past midnight), add 1440 to end
      //   so backend stores it as a wrap period.
      // - Validate no overlaps per day (hard-stop).
      const cleanedDays: ShiftDay[] = [];
      for (const d of weeklySchedule) {
        if (!d.periods || d.periods.length === 0) continue;
        const normalized = d.periods
          .filter((p) => Number.isFinite(p.startMinute) && Number.isFinite(p.endMinute))
          .map((p) => ({
            startMinute: p.startMinute,
            endMinute: p.endMinute <= p.startMinute ? p.endMinute + 1440 : p.endMinute,
          }));
        if (hasOverlap(normalized)) {
          toast.error(`روز ${DAYS_OF_WEEK_FA[d.dayOfWeek]}: بازه‌های شیفت تداخل دارند`);
          setSaving(false);
          return;
        }
        cleanedDays.push({ dayOfWeek: d.dayOfWeek, periods: sortPeriods(normalized) });
      }
      // Breaks: backend schema caps both ends at 0..1440 (no wrap past midnight) → start < end.
      for (const b of breaks) {
        if (!Number.isFinite(b.startMinute) || !Number.isFinite(b.endMinute) || b.endMinute <= b.startMinute) {
          toast.error('بازه استراحت نامعتبر است: زمان پایان باید بعد از زمان شروع باشد');
          setSaving(false);
          return;
        }
      }
      // Exceptions:
      // - 'extra' carries time periods; like the weekly editor, end <= start means wrap past
      //   midnight and is stored as endMinute + 1440 (backend allows endMinute up to 2880).
      // - 'leave'/'holiday' are whole-day → periods stripped.
      const cleanedExceptions: ShiftException[] = [];
      for (const ex of exceptions) {
        if (ex.type !== 'extra') {
          cleanedExceptions.push({ date: ex.date, type: ex.type, reason: ex.reason });
          continue;
        }
        const periods = (ex.periods || [])
          .filter((p) => Number.isFinite(p.startMinute) && Number.isFinite(p.endMinute))
          .map((p) => ({
            startMinute: p.startMinute,
            endMinute: p.endMinute <= p.startMinute ? p.endMinute + 1440 : p.endMinute,
          }));
        if (hasOverlap(periods)) {
          toast.error(
            `استثنای ${toPersianDigits(moment(ex.date).format('jYYYY/jMM/jDD'))}: بازه‌های اضافه‌کاری تداخل دارند`
          );
          setSaving(false);
          return;
        }
        cleanedExceptions.push({
          date: ex.date,
          type: ex.type,
          reason: ex.reason,
          periods: periods.length > 0 ? sortPeriods(periods) : undefined,
        });
      }
      await workAssignmentService.upsertProfile({
        userId,
        categories: catEntries,
        shift: { weeklySchedule: cleanedDays, breaks },
        exceptions: cleanedExceptions,
        isActive,
        notes,
        version,
      });
      toast.success('پروفایل ذخیره شد');
      onSaved();
      onClose();
    } catch (e: any) {
      toast.error(e?.message || 'خطا در ذخیره');
    } finally {
      setSaving(false);
    }
  };

  const visibleCats = useMemo(() => {
    const q = catSearch.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter(
      (c) => CATEGORY_LABELS[c.code].includes(q) || c.code.includes(q)
    );
  }, [categories, catSearch]);

  const selectedUser = adminUsers.find((u) => u._id === userId);
  const userInitial =
    selectedUser && selectedUser.name ? selectedUser.name.charAt(0).toUpperCase() : null;

  const tabCounts = {
    categories: catEntries.length,
    shifts: weeklySchedule.filter((d) => d.periods.length > 0).length,
    exceptions: exceptions.length,
  };

  return (
    <ResponsiveModal
      open={open}
      onClose={onClose}
      maxWidth="lg"
      disableClose={saving}
      icon={
        <div style={{
          width: 40, height: 40, borderRadius: '50%',
          background: 'linear-gradient(135deg, var(--color-primary), var(--color-primary-500))',
          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 16, fontWeight: 700,
        }}>
          {userInitial || <PersonIcon />}
        </div>
      }
      title={initial ? 'ویرایش پروفایل کاری' : 'ایجاد پروفایل کاری جدید'}
      subtitle={selectedUser ? `${selectedUser.name} · ${selectedUser.email}` : 'تعیین ساعت کار، حوزه‌ها و استثناهای ادمین'}
      actions={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, display: 'flex', gap: 8, alignItems: 'center' }}>
            {tab > 0 ? (
              <button
                className="edit-btn cancel"
                onClick={() => setTab(tab - 1)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <ChevronRightIcon fontSize="small" />
                قبلی
              </button>
            ) : (
              <div />
            )}
            <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginInlineStart: 8 }}>
              گام {tab + 1} از ۴
            </span>
          </div>
          <button className="edit-btn cancel" onClick={onClose}>انصراف</button>
          {tab < 3 ? (
            <button
              className="edit-btn submit"
              onClick={() => setTab(tab + 1)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              بعدی
              <ChevronLeftIcon fontSize="small" />
            </button>
          ) : (
            <button
              className="edit-btn submit"
              onClick={handleSave}
              disabled={saving}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              {saving && <CircularProgress size={16} style={{ color: '#fff' }} />}
              {initial ? 'ذخیره تغییرات' : 'ایجاد پروفایل'}
            </button>
          )}
        </div>
      }
    >
      {/* Stepper — compact */}
      <div style={{
        display: 'flex',
        alignItems: 'stretch',
        borderBottom: '1px solid var(--color-divider)',
        background: 'var(--color-bg-default)',
        flexShrink: 0,
        overflowX: 'auto',
        marginTop: -8,
        marginBottom: 4,
        borderRadius: 8,
        border: '1px solid var(--color-divider)',
      }}>
        {[
          { label: 'کاربر', icon: PersonIcon, badge: userId ? '✓' : '' },
          { label: 'حوزه‌ها', icon: CategoryIcon, badge: tabCounts.categories > 0 ? String(tabCounts.categories) : '' },
          { label: 'برنامه هفتگی', icon: ScheduleIcon, badge: tabCounts.shifts > 0 ? `${tabCounts.shifts} روز` : '' },
          { label: 'استثناها', icon: EventBusyIcon, badge: tabCounts.exceptions > 0 ? String(tabCounts.exceptions) : '' },
        ].map((step, idx) => {
          const StepIcon = step.icon;
          const isActive = idx === tab;
          const isCompleted = idx < tab;
          return (
            <button
              key={step.label}
              onClick={() => setTab(idx)}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '10px 16px',
                background: isActive ? 'var(--color-bg-paper)' : 'transparent',
                border: 'none',
                borderBottom: isActive ? '2px solid var(--color-primary)' : '2px solid transparent',
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: 13,
                fontWeight: isActive ? 700 : 500,
                color: isActive ? 'var(--color-primary)' : isCompleted ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                transition: 'all 0.15s',
                whiteSpace: 'nowrap',
                minHeight: 44,
              }}
              onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
              onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{
                width: 22, height: 22, borderRadius: '50%',
                background: isActive ? 'var(--color-primary)' : isCompleted ? 'var(--color-success)' : 'var(--color-bg-default)',
                color: isActive || isCompleted ? '#fff' : 'var(--color-text-secondary)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 600,
                border: isActive || isCompleted ? 'none' : '1px solid var(--color-border)',
              }}>
                {isCompleted ? '✓' : <StepIcon sx={{ fontSize: 13 }} />}
              </span>
              {step.label}
              {step.badge && (
                <span style={{
                  padding: '1px 7px',
                  background: isActive ? 'var(--color-primary)' : 'var(--color-text-disabled)',
                  color: '#fff',
                  borderRadius: 10,
                  fontSize: 10,
                  fontWeight: 600,
                }}>
                  {step.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div style={{ paddingTop: 24, paddingBottom: 8 }}>
        {/* === Tab 0: User & Basic === */}
        {tab === 0 && (
          <div style={{ maxWidth: 640, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div className="edit-card" style={{ borderRadius: 10, border: '1px solid var(--color-border)' }}>
              <div className="edit-card-header">اطلاعات کاربر</div>
              <div className="edit-card-body">
                <div className="edit-form-grid">
                  <div className="edit-field full">
                    <label>انتخاب ادمین <span style={{ color: 'var(--color-error-dark)' }}>*</span></label>
                    <Autocomplete
                      disabled={!!initial}
                      options={adminUsers}
                      getOptionLabel={(o) => `${o.name} — ${o.email}`}
                      value={adminUsers.find((u) => u._id === userId) || null}
                      onChange={(_, v) => setUserId(v?._id || '')}
                      renderInput={(p) => (
                        <TextField
                          {...p}
                          size="small"
                          placeholder="کاربر ادمین را انتخاب کنید..."
                          helperText={initial ? 'کاربر پس از ایجاد قابل تغییر نیست' : ''}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="edit-card" style={{ borderRadius: 10, border: '1px solid var(--color-border)' }}>
              <div className="edit-card-header">وضعیت و یادداشت</div>
              <div className="edit-card-body">
                <div className="edit-switches" style={{ marginBottom: 16 }}>
                  <label className="edit-switch-item">
                    <div>
                      <div className="edit-switch-label">وضعیت پروفایل</div>
                      <div className="edit-switch-desc">
                        {isActive ? 'فعال — ادمین در صف‌ها دیده می‌شود' : 'غیرفعال — ادمین در هیچ صفی نیست'}
                      </div>
                    </div>
                    <Switch size="small" checked={isActive} onChange={(_, v) => setIsActive(v)} />
                  </label>
                </div>
                <div className="edit-form-grid">
                  <div className="edit-field full">
                    <label>یادداشت (اختیاری)</label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="توضیح اضافی درباره‌ی این پروفایل..."
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* === Tab 1: Categories === */}
        {tab === 1 && (
          <div style={{ maxWidth: 900, margin: '0 auto' }}>
            {categories.length === 0 ? (
              <Alert severity="warning">
                لیست حوزه‌ها بارگذاری نشده. سرور باید restart بشه تا auto-seed اجرا بشه.
              </Alert>
            ) : (
              <>
                {/* Summary bar */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '14px 16px',
                  background: 'var(--color-bg-default)',
                  border: '1px solid var(--color-divider)',
                  borderRadius: 10,
                  marginBottom: 20,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 8,
                      background: catEntries.length > 0 ? 'var(--color-success-bg)' : 'var(--color-error-bg)',
                      color: catEntries.length > 0 ? 'var(--color-success-dark)' : 'var(--color-error-dark)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <CategoryIcon fontSize="small" />
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text-primary)' }}>
                        {catEntries.length} از {categories.length} حوزه انتخاب شده
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                        {catEntries.length === 0
                          ? 'حداقل یک حوزه انتخاب کنید'
                          : 'می‌توانید اولویت و حداکثر بار هر حوزه را تنظیم کنید'}
                      </div>
                    </div>
                  </div>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    background: 'var(--color-bg-paper)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 8,
                    padding: '4px 10px',
                    minWidth: 220,
                  }}>
                    <SearchIcon fontSize="small" style={{ color: 'var(--color-text-disabled)' }} />
                    <input
                      type="text"
                      value={catSearch}
                      onChange={(e) => setCatSearch(e.target.value)}
                      placeholder="جستجوی حوزه..."
                      style={{
                        border: 'none', outline: 'none', background: 'transparent',
                        fontSize: 13, flex: 1, padding: '6px 0',
                        color: 'var(--color-text-primary)', fontFamily: 'inherit',
                      }}
                    />
                    {catSearch && (
                      <button
                        onClick={() => setCatSearch('')}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: 'var(--color-text-secondary)', padding: 2,
                          display: 'flex', alignItems: 'center',
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Selected Categories */}
                {catEntries.length > 0 && (
                  <div style={{ marginBottom: 24 }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
                      fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)',
                    }}>
                      <CheckCircleIcon sx={{ fontSize: 18, color: 'var(--color-success-dark)' }} />
                      حوزه‌های انتخاب شده
                    </div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                      gap: 12,
                    }}>
                      {catEntries.map((entry) => {
                        const cat = categories.find((c) => c.code === entry.code);
                        if (!cat) return null;
                        return (
                          <div
                            key={entry.code}
                            style={{
                              background: 'var(--color-bg-paper)',
                              border: '2px solid var(--color-primary)',
                              borderRadius: 10,
                              padding: 12,
                              position: 'relative',
                              boxShadow: '0 1px 3px rgba(178, 83, 115, 0.12)',
                            }}
                          >
                            <div style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              marginBottom: 10,
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{
                                  width: 24, height: 24, borderRadius: '50%',
                                  background: 'var(--color-primary)', color: '#fff',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                  <CheckCircleIcon sx={{ fontSize: 18 }} />
                                </div>
                                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text-primary)' }}>
                                  {CATEGORY_LABELS[cat.code as WorkCategoryCode]}
                                </span>
                              </div>
                              <Tooltip title="حذف">
                                <button
                                  onClick={() => toggleCategory(cat.code as WorkCategoryCode)}
                                  style={{
                                    width: 26, height: 26, borderRadius: 6, border: 'none',
                                    background: 'transparent', color: 'var(--color-error-dark)', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  }}
                                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-error-bg)')}
                                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                                >
                                  <CloseIcon fontSize="small" />
                                </button>
                              </Tooltip>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                              <div>
                                <label style={{
                                  display: 'block', fontSize: 11, fontWeight: 500,
                                  color: 'var(--color-text-secondary)', marginBottom: 4,
                                }}>
                                  اولویت (1=بالا)
                                </label>
                                <input
                                  type="number"
                                  min={1}
                                  max={5}
                                  value={entry.priority}
                                  onChange={(e) =>
                                    updateCategoryEntry(cat.code as WorkCategoryCode, {
                                      priority: Number(e.target.value),
                                    })
                                  }
                                  style={{
                                    width: '100%', height: 36, padding: '0 10px',
                                    border: '1px solid var(--color-border)', borderRadius: 6,
                                    fontSize: 13, fontFamily: 'inherit',
                                    background: 'var(--color-bg-default)',
                                    color: 'var(--color-text-primary)',
                                    textAlign: 'center', direction: 'ltr',
                                  }}
                                />
                              </div>
                              <div>
                                <label style={{
                                  display: 'block', fontSize: 11, fontWeight: 500,
                                  color: 'var(--color-text-secondary)', marginBottom: 4,
                                }}>
                                  حداکثر بار (0=∞)
                                </label>
                                <input
                                  type="number"
                                  min={0}
                                  value={entry.maxConcurrentLoad}
                                  onChange={(e) =>
                                    updateCategoryEntry(cat.code as WorkCategoryCode, {
                                      maxConcurrentLoad: Number(e.target.value),
                                    })
                                  }
                                  style={{
                                    width: '100%', height: 36, padding: '0 10px',
                                    border: '1px solid var(--color-border)', borderRadius: 6,
                                    fontSize: 13, fontFamily: 'inherit',
                                    background: 'var(--color-bg-default)',
                                    color: 'var(--color-text-primary)',
                                    textAlign: 'center', direction: 'ltr',
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Unselected Categories */}
                {(() => {
                  const unselected = visibleCats.filter(
                    (c) => !catEntries.some((e) => e.code === c.code)
                  );
                  if (unselected.length === 0) return null;
                  return (
                    <div>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
                        fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)',
                      }}>
                        <AddIcon sx={{ fontSize: 18, color: 'var(--color-text-secondary)' }} />
                        سایر حوزه‌های در دسترس
                        <span style={{
                          fontSize: 11, fontWeight: 500,
                          padding: '2px 8px', borderRadius: 10,
                          background: 'var(--color-bg-default)',
                          color: 'var(--color-text-secondary)',
                        }}>
                          {unselected.length}
                        </span>
                      </div>
                      <div style={{
                        display: 'flex', flexWrap: 'wrap', gap: 8,
                        padding: 16,
                        background: 'var(--color-bg-default)',
                        borderRadius: 10,
                        border: '1px dashed var(--color-divider)',
                      }}>
                        {unselected.map((cat) => (
                          <button
                            key={cat.code}
                            onClick={() => toggleCategory(cat.code as WorkCategoryCode)}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 6,
                              padding: '8px 14px',
                              background: 'var(--color-bg-paper)',
                              border: '1px solid var(--color-border)',
                              borderRadius: 20,
                              fontSize: 13, fontWeight: 500,
                              cursor: 'pointer',
                              color: 'var(--color-text-primary)',
                              fontFamily: 'inherit',
                              transition: 'all 0.15s',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = 'var(--color-primary)';
                              e.currentTarget.style.background = 'var(--color-primary-bg)';
                              e.currentTarget.style.color = 'var(--color-primary)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = 'var(--color-border)';
                              e.currentTarget.style.background = 'var(--color-bg-paper)';
                              e.currentTarget.style.color = 'var(--color-text-primary)';
                            }}
                          >
                            <AddIcon sx={{ fontSize: 14 }} />
                            {CATEGORY_LABELS[cat.code as WorkCategoryCode]}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* No results */}
                {catSearch && visibleCats.filter((c) => !catEntries.some((e) => e.code === c.code)).length === 0 && catEntries.length === 0 && (
                  <div style={{
                    textAlign: 'center', padding: 32,
                    color: 'var(--color-text-secondary)', fontSize: 13,
                  }}>
                    حوزه‌ای با این جستجو یافت نشد
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* === Tab 2: Weekly Shift === */}
        {tab === 2 && (
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <WeeklyShiftEditor schedule={weeklySchedule} onChange={setWeeklySchedule} />
            <BreaksEditor breaks={breaks} onChange={setBreaks} />
          </div>
        )}

        {/* === Tab 3: Exceptions === */}
        {tab === 3 && (
          <div style={{ maxWidth: 900, margin: '0 auto' }}>
            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 12, padding: '14px 16px',
              background: 'var(--color-bg-default)',
              border: '1px solid var(--color-divider)',
              borderRadius: 10,
              marginBottom: 20,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 8,
                  background: 'var(--color-warning-bg)', color: 'var(--color-warning-dark)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <EventBusyIcon fontSize="small" />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text-primary)' }}>
                    استثناهای تقویم ({toPersianDigits(String(exceptions.length))})
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                    مرخصی، اضافه‌کاری، یا تعطیلی اختصاصی در تاریخ‌های خاص
                  </div>
                </div>
              </div>
              <button
                onClick={() =>
                  setExceptions([
                    ...exceptions,
                    { date: new Date().toISOString(), type: 'leave', reason: '' },
                  ])
                }
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px',
                  background: 'var(--color-primary)', color: '#fff',
                  border: 'none', borderRadius: 8,
                  fontSize: 13, fontWeight: 500,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-primary-dark)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--color-primary)')}
              >
                <AddIcon fontSize="small" />
                افزودن استثنا
              </button>
            </div>

            {/* Empty state */}
            {exceptions.length === 0 && (
              <div style={{
                padding: 40, textAlign: 'center',
                background: 'var(--color-bg-paper)',
                border: '1px dashed var(--color-divider)',
                borderRadius: 10,
                color: 'var(--color-text-secondary)',
              }}>
                <div style={{
                  width: 56, height: 56, borderRadius: '50%',
                  background: 'var(--color-bg-default)',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 12,
                }}>
                  <EventBusyIcon sx={{ fontSize: 28, color: 'var(--color-text-disabled)' }} />
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4, color: 'var(--color-text-primary)' }}>
                  استثنایی تعریف نشده
                </div>
                <div style={{ fontSize: 12 }}>
                  برای تعیین مرخصی، اضافه‌کاری یا تعطیلی در تاریخ‌های خاص، از دکمه «افزودن استثنا» استفاده کنید.
                </div>
              </div>
            )}

            {/* Exceptions list */}
            {exceptions.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {exceptions.map((ex, idx) => {
                  const meta = EXCEPTION_TYPE_META[ex.type] || EXCEPTION_TYPE_META.leave;
                  return (
                    <div
                      key={idx}
                      style={{
                        background: 'var(--color-bg-paper)',
                        border: `1px solid var(--color-divider)`,
                        borderInlineStart: `4px solid ${meta.color}`,
                        borderRadius: 10,
                        padding: 14,
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 10,
                        alignItems: 'center',
                      }}>
                        {/* Index circle */}
                        <div style={{
                          flexShrink: 0,
                          width: 32, height: 32, borderRadius: '50%',
                          background: meta.bg, color: meta.color,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 15,
                        }}>
                          {meta.icon}
                        </div>

                        {/* Date picker */}
                        <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                          <label style={{
                            display: 'block', fontSize: 11, fontWeight: 500,
                            color: 'var(--color-text-secondary)', marginBottom: 4,
                          }}>
                            تاریخ
                          </label>
                          <JalaliDatePicker
                            value={ex.date}
                            onChange={(iso) => {
                              const next = [...exceptions];
                              next[idx].date = iso;
                              setExceptions(next);
                            }}
                          />
                        </div>

                        {/* Type */}
                        <div style={{ flex: '1 1 120px', minWidth: 0 }}>
                          <label style={{
                            display: 'block', fontSize: 11, fontWeight: 500,
                            color: 'var(--color-text-secondary)', marginBottom: 4,
                          }}>
                            نوع
                          </label>
                          <select
                            value={ex.type}
                            onChange={(e) => {
                              const newType = e.target.value as ShiftException['type'];
                              setExceptions(
                                exceptions.map((item, i) => {
                                  if (i !== idx) return item;
                                  return {
                                    ...item,
                                    type: newType,
                                    // «اضافه‌کاری» بدون بازه یعنی دسترسی کل روز (۲۴ ساعت)؛
                                    // برای جلوگیری از دسترسی ناخواسته، یک بازه پیش‌فرض می‌سازیم.
                                    periods:
                                      newType === 'extra'
                                        ? item.periods && item.periods.length > 0
                                          ? item.periods
                                          : [{ startMinute: 540, endMinute: 1020 }]
                                        : undefined,
                                  };
                                })
                              );
                            }}
                            style={{
                              width: '100%', height: 38, padding: '0 10px',
                              border: '1px solid var(--color-border)', borderRadius: 6,
                              fontSize: 13, fontFamily: 'inherit',
                              background: 'var(--color-bg-paper)',
                              color: 'var(--color-text-primary)',
                              cursor: 'pointer',
                            }}
                          >
                            <option value="leave">مرخصی</option>
                            <option value="extra">اضافه‌کاری</option>
                            <option value="holiday">تعطیلی</option>
                          </select>
                        </div>

                        {/* Reason */}
                        <div style={{ flex: '2 1 160px', minWidth: 0 }}>
                          <label style={{
                            display: 'block', fontSize: 11, fontWeight: 500,
                            color: 'var(--color-text-secondary)', marginBottom: 4,
                          }}>
                            دلیل (اختیاری)
                          </label>
                          <input
                            type="text"
                            value={ex.reason || ''}
                            onChange={(e) => {
                              const next = [...exceptions];
                              next[idx].reason = e.target.value;
                              setExceptions(next);
                            }}
                            placeholder="توضیح..."
                            style={{
                              width: '100%', height: 38, padding: '0 10px',
                              border: '1px solid var(--color-border)', borderRadius: 6,
                              fontSize: 13, fontFamily: 'inherit',
                              background: 'var(--color-bg-paper)',
                              color: 'var(--color-text-primary)',
                              boxSizing: 'border-box',
                            }}
                          />
                        </div>

                        {/* Delete */}
                        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'flex-end', alignSelf: 'flex-end' }}>
                          <button
                            onClick={() => setExceptions(exceptions.filter((_, i) => i !== idx))}
                            style={{
                              width: 38, height: 38, borderRadius: 8, border: 'none',
                              background: 'transparent', cursor: 'pointer',
                              color: 'var(--color-error-dark)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-error-bg)')}
                            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                            title="حذف این استثنا"
                          >
                            <DeleteIcon fontSize="small" />
                          </button>
                        </div>
                      </div>

                      {/* Periods editor — only for 'extra' (اضافه‌کاری). leave/holiday = whole day. */}
                      {ex.type === 'extra' && (() => {
                        const periods = ex.periods || [];
                        // Same wrap semantics as the weekly editor: end <= start → wraps past midnight.
                        const normalized = periods.map((p) => ({
                          startMinute: p.startMinute,
                          endMinute: p.endMinute <= p.startMinute ? p.endMinute + 1440 : p.endMinute,
                        }));
                        const overlap = hasOverlap(normalized);
                        return (
                          <div style={{
                            marginTop: 12, paddingTop: 12,
                            borderTop: '1px dashed var(--color-divider)',
                          }}>
                            <div style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              gap: 8, marginBottom: 8,
                            }}>
                              <span style={{
                                fontSize: 11, fontWeight: 600,
                                color: 'var(--color-text-secondary)',
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                              }}>
                                <AccessTimeIcon sx={{ fontSize: 14 }} />
                                بازه‌های اضافه‌کاری
                              </span>
                              <button
                                onClick={() => addExceptionPeriod(idx)}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 4,
                                  padding: '4px 10px',
                                  background: 'transparent',
                                  border: '1px dashed var(--color-border)',
                                  borderRadius: 6,
                                  fontSize: 11, fontWeight: 500,
                                  cursor: 'pointer',
                                  color: 'var(--color-text-secondary)',
                                  fontFamily: 'inherit',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.borderColor = 'var(--color-primary)';
                                  e.currentTarget.style.color = 'var(--color-primary)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.borderColor = 'var(--color-border)';
                                  e.currentTarget.style.color = 'var(--color-text-secondary)';
                                }}
                              >
                                <AddIcon sx={{ fontSize: 14 }} />
                                افزودن بازه
                              </button>
                            </div>

                            {periods.length === 0 && (
                              <div style={{
                                padding: '6px 10px',
                                background: 'rgba(245, 158, 11, 0.1)',
                                color: 'var(--color-warning-dark)',
                                borderRadius: 6, fontSize: 11,
                                display: 'flex', alignItems: 'center', gap: 6,
                              }}>
                                ⚠ بدون بازه زمانی، اضافه‌کاری برای کل روز (۲۴ ساعت) اعمال می‌شود
                              </div>
                            )}

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              {periods.map((p, pIdx) => {
                                const wraps = p.endMinute <= p.startMinute;
                                return (
                                  <div
                                    key={pIdx}
                                    style={{
                                      display: 'flex', alignItems: 'center', gap: 6,
                                      maxWidth: 420, minWidth: 0,
                                    }}
                                  >
                                    <PersianTimeInput
                                      value={p.startMinute}
                                      onChange={(v) => updateExceptionPeriod(idx, pIdx, { startMinute: v })}
                                    />
                                    <span style={{
                                      fontSize: 13, color: 'var(--color-text-secondary)',
                                      fontWeight: 600, flexShrink: 0,
                                    }}>←</span>
                                    <PersianTimeInput
                                      value={p.endMinute}
                                      onChange={(v) => updateExceptionPeriod(idx, pIdx, { endMinute: v })}
                                    />
                                    {wraps && (
                                      <span style={{
                                        padding: '2px 8px',
                                        background: 'var(--color-primary-bg)',
                                        color: 'var(--color-primary)',
                                        borderRadius: 10,
                                        fontSize: 10, fontWeight: 600,
                                        flexShrink: 0, whiteSpace: 'nowrap',
                                      }}>
                                        تا فردا
                                      </span>
                                    )}
                                    <button
                                      onClick={() => removeExceptionPeriod(idx, pIdx)}
                                      style={{
                                        width: 28, height: 28, borderRadius: 6, border: 'none',
                                        background: 'transparent', cursor: 'pointer',
                                        color: 'var(--color-error-dark)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0,
                                      }}
                                      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-error-bg)')}
                                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                                      title="حذف بازه"
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>

                            {overlap && (
                              <div style={{
                                marginTop: 8, padding: '6px 10px',
                                background: 'var(--color-error-bg)', color: 'var(--color-error-dark)',
                                borderRadius: 6, fontSize: 11,
                                display: 'flex', alignItems: 'center', gap: 6,
                              }}>
                                ⚠ بازه‌های اضافه‌کاری با هم تداخل دارند
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </ResponsiveModal>
  );
};

const AdminWorkProfilesPage: React.FC = () => {
  const [rows, setRows] = useState<AdminWorkProfile[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [categories, setCategories] = useState<WorkCategory[]>([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<AdminWorkProfile | undefined>();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState<AdminWorkProfile | undefined>();
  const [deleteLoading, setDeleteLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res: any = await workAssignmentService.listProfiles({
        page: page + 1,
        limit: pageSize,
        search: search || undefined,
      });
      // api.ts strips the outer {status,data} envelope → we get { docs, pagination }
      setRows(res?.docs || []);
      setTotal(res?.pagination?.total || 0);
    } catch (e: any) {
      toast.error(e?.message || 'خطا در بارگذاری');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    workAssignmentService.listCategories().then(setCategories).catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, search]);

  const openDeleteDialog = (row: AdminWorkProfile) => {
    setToDelete(row);
    setDeleteOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteOpen(false);
    setToDelete(undefined);
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    const u: any = toDelete.userId;
    const userId = typeof u === 'string' ? u : u._id;
    setDeleteLoading(true);
    try {
      await workAssignmentService.deleteProfile(userId);
      toast.success('حذف شد');
      closeDeleteDialog();
      load();
    } catch (e: any) {
      toast.error(e?.message || 'خطا');
    } finally {
      setDeleteLoading(false);
    }
  };

  const activeCount = rows.filter(r => r.isActive).length;
  const onlineCount = rows.filter(r => {
    const s = (r as any).liveStatus as keyof typeof STATUS_LABELS;
    return s === 'on_shift';
  }).length;

  const statusTagClassOf = (color: string) =>
    color === 'success' ? 'tag-active'
    : color === 'warning' ? 'tag-warning'
    : color === 'error' ? 'tag-danger' : 'tag-inactive';

  const columns: Column<AdminWorkProfile>[] = [
    {
      id: 'userId',
      label: 'ادمین',
      priority: 1,
      format: (u: any) => (
        <div className="entity-info-cell">
          <div className="entity-image">
            {typeof u === 'object' && u?.name ? u.name.charAt(0) : <PersonIcon />}
          </div>
          <div className="entity-info" style={{ minWidth: 0 }}>
            <span className="entity-name">{typeof u === 'object' ? u?.name : '—'}</span>
            {typeof u === 'object' && u?.email
              ? <CopyableText value={u.email} mono maxWidth={200} />
              : <span className="entity-desc">{typeof u === 'object' ? u?.email : u}</span>}
          </div>
        </div>
      ),
    },
    {
      id: 'categories',
      label: 'حوزه‌ها',
      priority: 3,
      format: (_v, row) => (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {(row.categories || []).slice(0, 3).map((c: ProfileCategory) => (
            <span key={c.code} className="tag tag-sm tag-platform">
              {CATEGORY_LABELS[c.code]} (p{c.priority})
            </span>
          ))}
          {row.categories && row.categories.length > 3 && (
            <span className="tag tag-sm tag-admin">+{row.categories.length - 3}</span>
          )}
        </div>
      ),
    },
    {
      id: 'shift',
      label: 'برنامه هفتگی',
      priority: 5,
      detail: true,
      format: (_v, row) => (
        <span style={{ fontSize: 12, color: 'var(--color-text-secondary)', whiteSpace: 'pre-wrap' }}>
          {summarizeWeekly(row.shift?.weeklySchedule)}
        </span>
      ),
    },
    {
      id: 'liveStatus',
      label: 'وضعیت فعلی',
      priority: 2,
      align: 'center',
      format: (_v, row) => {
        const s = (row as any).liveStatus as keyof typeof STATUS_LABELS;
        const info = STATUS_LABELS[s] || STATUS_LABELS.off_shift;
        return <span className={`tag tag-sm ${statusTagClassOf(info.color)}`}>{info.label}</span>;
      },
    },
    {
      id: 'isActive',
      label: 'فعال',
      priority: 4,
      align: 'center',
      format: (v: boolean) => (
        <span className={`tag tag-sm ${v ? 'tag-active' : 'tag-inactive'}`}>
          {v ? 'بله' : 'خیر'}
        </span>
      ),
    },
  ];

  const renderActions = (row: AdminWorkProfile) => (
    <>
      <Tooltip title="ویرایش" arrow>
        <button className="action-btn edit" onClick={() => { setEditing(row); setEditorOpen(true); }}>
          <EditIcon />
        </button>
      </Tooltip>
      <Tooltip title="حذف" arrow>
        <button className="action-btn delete" onClick={() => openDeleteDialog(row)}>
          <DeleteIcon />
        </button>
      </Tooltip>
    </>
  );

  return (
    <div className="entity-page">
      {/* Header */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">شیفت و حوزه‌ی ادمین‌ها</h1>
          <p className="entity-subtitle">مدیریت ساعت کار، شیفت، و حوزه‌های مسئولیت هر ادمین</p>
        </div>
        <div className="header-actions">
          <button className="header-btn refresh" onClick={load} title="بروزرسانی">
            <RefreshIcon fontSize="small" />
          </button>
          <button className="header-btn primary" onClick={() => { setEditing(undefined); setEditorOpen(true); }}>
            <AddIcon fontSize="small" />
            پروفایل جدید
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="entity-stats">
        <div className="entity-stat-card">
          <div className="entity-stat-icon blue"><GroupIcon /></div>
          <div>
            <p className="entity-stat-label">کل پروفایل‌ها</p>
            <p className="entity-stat-value">{total}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon green"><CheckCircleIcon /></div>
          <div>
            <p className="entity-stat-label">فعال</p>
            <p className="entity-stat-value">{activeCount}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon purple"><WorkIcon /></div>
          <div>
            <p className="entity-stat-label">در شیفت</p>
            <p className="entity-stat-value">{onlineCount}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="entity-filters">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">
              <SearchIcon fontSize="small" />
              جستجو
            </label>
            <div className="filter-search-wrap">
              <input
                className="filter-field"
                type="text"
                placeholder="جستجوی نام یا ایمیل..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              />
              <span className="filter-search-icon">
                <SearchIcon fontSize="small" />
              </span>
            </div>
          </div>

          <div className="filter-group filter-group-actions">
            {search && (
              <button className="filter-btn outline" onClick={() => { setSearch(''); setPage(0); }}>
                پاک
              </button>
            )}
          </div>
        </div>
      </div>

      {categories.length === 0 && !loading && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          لیست حوزه‌های کاری بارگذاری نشده است. لطفاً سرور را restart کنید تا auto-seed اجرا شود.
        </Alert>
      )}

      {/* Table */}
      <div className="entity-table-wrapper">
        <ResponsiveTable<AdminWorkProfile>
          columns={columns}
          data={rows}
          keyField="_id"
          loading={loading}
          emptyMessage="موردی یافت نشد"
          renderActions={renderActions}
          pagination
          page={page}
          rowsPerPage={pageSize}
          totalCount={total}
          rowsPerPageOptions={[10, 20, 50]}
          onPageChange={(p) => setPage(p)}
          onRowsPerPageChange={(rpp) => { setPageSize(rpp); setPage(0); }}
        />
      </div>

      <ProfileEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        initial={editing}
        categories={categories}
        onSaved={load}
      />

      {/* Delete Dialog */}
      <ConfirmDialog
        open={deleteOpen}
        title="حذف پروفایل کاری"
        message={
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            آیا از حذف پروفایل <strong>«{typeof toDelete?.userId === 'object' ? (toDelete.userId as any).name : 'ادمین'}»</strong> اطمینان دارید؟
            <br />این عملیات غیرقابل بازگشت است و تمام تنظیمات شیفت و حوزه‌های این ادمین پاک می‌شود.
          </Typography>
        }
        confirmText="حذف پروفایل"
        confirmColor="error"
        loading={deleteLoading}
        onConfirm={handleDelete}
        onCancel={closeDeleteDialog}
      />
    </div>
  );
};

export default AdminWorkProfilesPage;
